/** A trip reduced to what the distance figure needs from it. */
export interface TripDistance {
  /** Distance in kilometres, as Geotab reports it on `Trip`. */
  distance: number;
}

/**
 * How far a vehicle drove in the period.
 *
 * Read off the trips rather than off the odometer at each end of the period, which is what this
 * page used to do: an odometer reading has to exist at both instants, and where one is missing —
 * or where the later one reads lower after a device swap — the whole distance came back as
 * unknown, which for a fuel-cell fleet is most of the period.
 *
 * @param trips - The trips of one vehicle, or `null` when they have not been read yet.
 * @returns The distance, or `null` while it is not known. Zero means the vehicle stood still,
 * which is a different thing and has to stay distinguishable from it.
 */
export function tripDistanceKm(trips: TripDistance[] | null | undefined): number | null {
  if (!trips) {
    return null;
  }

  return trips.reduce(
    (total, trip) => (Number.isFinite(trip.distance) ? total + Math.max(0, trip.distance) : total),
    0,
  );
}

/**
 * The average consumption over the period, in kg per 100 km.
 *
 * Below the minimum distance it is not reported at all. Over a few kilometres the figure is
 * dominated by whatever the tank sensor happened to read at each end, and a vehicle that moved
 * across the yard will otherwise show a consumption ten times its real one — next to the
 * vehicles that did a week of work, in the same column.
 *
 * @param usedKg - Hydrogen used over the period.
 * @param distanceKm - Distance driven over the period.
 * @param minDistanceKm - Distance below which no average is reported. Zero reports every vehicle.
 */
export function averageConsumptionKgPer100Km(
  usedKg: number | null,
  distanceKm: number | null,
  minDistanceKm: number,
): number | null {
  if (usedKg == null || distanceKm == null || distanceKm <= 0) {
    return null;
  }
  if (distanceKm < minDistanceKm) {
    return null;
  }

  return (usedKg / distanceKm) * 100;
}

/**
 * Whether the vehicle drove without reporting its tank level.
 *
 * The one case where the figures on the row are not just missing but misleading: the distance is
 * real and the consumption is not, so the row reads as an efficient vehicle. Everything derived
 * from the tank level is greyed out on the strength of this.
 *
 * @param distanceKm - Distance driven over the period, or `null` while it is not known.
 * @param hasLevelReading - Whether a tank level arrived for the period.
 */
export function isStaleReading(distanceKm: number | null, hasLevelReading: boolean): boolean {
  return distanceKm != null && distanceKm > 0 && !hasLevelReading;
}

/** The value of the exported "level reported" column: the inverse of the stale flag. */
export function levelReportedValue(row: { isStale: boolean }): boolean {
  return !row.isStale;
}
